// src/features/services/sales/api/productionOrderApi.ts
// ─────────────────────────────────────────────────────────────────────────────
// Production / Work Orders — FE API layer.
// All calls go through the BFF; zero orchestrator internals leak here.
// ─────────────────────────────────────────────────────────────────────────────
import { bff } from '@/lib/http/bff';

const BASE = '/production-orders';

// ── Types ────────────────────────────────────────────────────────────────────

export interface WorkOrderComponent {
  sku: string;
  required_qty: number;
  unit_qty: number;
  unit: string;
  label: string;
}

export interface WorkOrder {
  serial_number: string;
  wo_scope_key: string;
  id_scope: number | null;
  status: string;            // draft | in_progress | completed | cancelled
  finished_sku: string;
  bom_key: string;
  quantity: number;
  label: string;
  notes: string;
  components: WorkOrderComponent[];
  started_at: string | null;
  completed_at: string | null;
  duration_minutes: number | null;
  yield_quantity: number | null;
  scrap_quantity: number | null;
  created_by: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CreateWorkOrderPayload {
  finished_sku: string;
  quantity: number;
  label?: string;
  notes?: string;
}

export interface CreateWorkOrderResponse {
  serial_number: string;
  wo_scope_key: string;
  status: string;
  finished_sku: string;
  bom_key: string;
  quantity: number;
  label: string;
  components: WorkOrderComponent[];
}

export interface CompleteWorkOrderPayload {
  yield_quantity?: number;
  scrap_quantity?: number;
  duration_minutes?: number;
  notes?: string;
}

export interface WorkOrderListResponse {
  items: WorkOrder[];
  count: number;
  cursor: string | null;
}

// ── Query Keys ───────────────────────────────────────────────────────────────

export const QK_WORK_ORDERS = ['production', 'work-orders'] as const;

// ── API functions ────────────────────────────────────────────────────────────

export async function createWorkOrderApi(
  payload: CreateWorkOrderPayload,
): Promise<CreateWorkOrderResponse> {
  return bff.post<CreateWorkOrderResponse, CreateWorkOrderPayload>(BASE, payload);
}

export async function fetchWorkOrdersApi(
  params?: { status?: string; limit?: number; cursor?: string },
): Promise<WorkOrderListResponse> {
  return bff.get<WorkOrderListResponse>(BASE, params as any);
}

export async function fetchWorkOrderApi(serialNumber: string): Promise<WorkOrder> {
  return bff.get<WorkOrder>(`${BASE}/${encodeURIComponent(serialNumber)}`);
}

export async function startWorkOrderApi(serialNumber: string): Promise<WorkOrder> {
  return bff.post<WorkOrder, undefined>(
    `${BASE}/${encodeURIComponent(serialNumber)}/start`,
    undefined,
  );
}

export async function completeWorkOrderApi(
  serialNumber: string,
  body: CompleteWorkOrderPayload,
): Promise<WorkOrder> {
  return bff.post<WorkOrder, CompleteWorkOrderPayload>(
    `${BASE}/${encodeURIComponent(serialNumber)}/complete`,
    body,
  );
}

export async function cancelWorkOrderApi(serialNumber: string): Promise<WorkOrder> {
  return bff.post<WorkOrder, undefined>(
    `${BASE}/${encodeURIComponent(serialNumber)}/cancel`,
    undefined,
  );
}

// ── Inventory ────────────────────────────────────────────────────────────

export type SkuInventory = Record<string, number>;

export const QK_INVENTORY = ['production', 'inventory'] as const;

export async function fetchInventoryApi(): Promise<SkuInventory> {
  return bff.get<SkuInventory>(`${BASE}/inventory`);
}
